import React, { Component } from 'react'
import Autosuggest from 'react-autosuggest'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import TextField from '@material-ui/core/TextField/index'
import Paper from '@material-ui/core/Paper/index'
import CircularProgress from '@material-ui/core/CircularProgress/index'
import InputAdornment from '@material-ui/core/InputAdornment/index'
import MenuItem from '@material-ui/core/MenuItem/index'
import { withStyles } from '@material-ui/core/styles/index'
import HomeWorkIcon from '@material-ui/icons/HomeWork'
import { withTranslation } from 'react-i18next'

const styles = theme => ({
    root: {
        flexGrow: 1,
        padding: theme.spacing(0.5),
        zIndex: 10
    },
    container: {
        position: 'relative'
    },
    suggestionsContainerOpen: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing(0.5),
        left: 0,
        right: 0,
        padding: theme.spacing(1, 0),
        border: theme.input ? theme.input.border + '!important' : null,
        borderRadius: theme.input ? theme.input.borderRadius + 'px!important' : null,
        boxShadow: theme.input ? theme.input.boxShadow + '!important' : null
    },
    suggestion: {
        display: 'block'
    },
    suggestionsList: {
        margin: 0,
        padding: 0,
        listStyleType: 'none'
    },
    divider: {
        height: theme.spacing(2)
    },
    textField: {
        marginTop: 0,
        '& fieldset': {
            border: theme.input ? theme.input.border + '!important' : null,
            borderRadius: theme.input ? theme.input.borderRadius + 'px!important' : null,
            boxShadow: theme.input ? theme.input.boxShadow + '!important' : null
        }
    },
    suggestionItem: {
        width: '100%',
        ...(!theme.isMobile && {
            display: 'flex'
        })
    },
    suggestionCompanyName: {
        whiteSpace: 'normal',
        lineHeight: 1.15
    },
    suggestionBusinessId: {
        marginLeft: 'auto',
        marginTop: 2,
        lineHeight: 1,
        color: 'grey',
        ...(!theme.isMobile && {
            paddingLeft: 8
        }),
        ...(theme.isMobile && {
            fontSize: '0.8rem'
        })
    },
    inputAdornement: {
        color: '#979797',
        marginRight: theme.spacing(1.5)
    }
})

class AutoSuggest extends Component {
    state = { single: '', stateSuggestions: [], selectedSuggestion: null, allSuggestions: [], isLoading: false }

    componentDidMount() {
        this.updateTimeout = null
    }

    handleSuggestionsFetchRequested = ({ value }) => {
        if (!value || value.length < 3) {
            return
        }
        this.getSuggestions(value)
    }

    handleSuggestionsClearRequested = () => {
        this.setSuggestions([])
    }

    handleChange = name => (event, { newValue }) => {
        this.setState({
            ...this.state,
            [name]: newValue
        })
    }

    setSuggestions = value => {
        this.setState({ stateSuggestions: value, allSuggestions: [...this.state.allSuggestions, ...value] })
    }

    getSuggestions = value => {
        clearTimeout(this.updateTimeout)
        this.updateTimeout = setTimeout(() => {
            this.setState({ isLoading: true })
            this.props
                .getSuggestions(encodeURIComponent(value))
                .then(data => {
                    this.setState({ isLoading: false })
                    if (data.notFound) {
                        this.props.companyNotFound(value)
                        this.setSuggestions([])
                        return
                    }
                    this.props.companiesFound(value)
                    this.setSuggestions(data)
                })
                .catch(err => {
                    this.setState({ isLoading: false })
                })
        }, 300)
    }

    onSuggestionSelected = (event, { suggestion }) => {
        this.props.onChange(suggestion)
        this.setState({ single: '' })
    }

    render() {
        const { classes, theme, question, t } = this.props

        const { single, stateSuggestions, isLoading } = this.state

        const getSuggestionValue = suggestion => {
            return suggestion.name
        }

        const renderInputComponent = inputProps => {
            const { classes, inputRef = () => {}, ref, ...other } = inputProps

            return (
                <TextField
                    fullWidth
                    error={this.props.valid === false}
                    disabled={this.props.question.disabled ?? false}
                    variant={theme.inputVariant}
                    className={classes.textField + ' inputText ' + (this.props.question.variant === 'outlined' ? 'outlinedInputText' : '')}
                    InputProps={{
                        inputRef: node => {
                            ref(node)
                            inputRef(node)
                        },
                        classes: {
                            input: classes.input
                        },
                        startAdornment: (
                            <InputAdornment position="start" className={classes.inputAdornement}>
                                <HomeWorkIcon />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <>
                                {isLoading && (
                                    <InputAdornment position="end">
                                        <CircularProgress className={classes.loadingIcon} size={20} />
                                    </InputAdornment>
                                )}
                            </>
                        )
                    }}
                    {...other}
                />
            )
        }

        const renderSuggestion = (suggestion, { query, isHighlighted }) => {
            const matches = match(suggestion.name, query)
            const parts = parse(suggestion.name, matches)

            return (
                <MenuItem selected={isHighlighted} component="div">
                    <div className={classes.suggestionItem}>
                        <div className={classes.suggestionCompanyName}>
                            {parts.map(part => (
                                <span key={part.text} style={{ fontWeight: part.highlight ? 500 : 400 }}>
                                    {part.text}
                                </span>
                            ))}
                        </div>
                        <div className={classes.suggestionBusinessId}>{suggestion.business_id}</div>
                    </div>
                </MenuItem>
            )
        }

        const autosuggestProps = {
            renderInputComponent,
            suggestions: stateSuggestions,
            onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested,
            onSuggestionsClearRequested: this.handleSuggestionsClearRequested,
            getSuggestionValue,
            renderSuggestion,
            onSuggestionSelected: this.onSuggestionSelected
        }

        return (
            <div className={classes.root}>
                <Autosuggest
                    {...autosuggestProps}
                    inputProps={{
                        classes,
                        id: 'react-autosuggest-simple',
                        label: question && question.title ? question.title + ' *' : t('Yrityksen nimi tai Y-tunnus') + ' *',
                        placeholder: question && question.title ? question.title : t('Ehdotan vaihtoehtoja'),
                        value: single,
                        onChange: this.handleChange('single')
                    }}
                    theme={{
                        container: classes.container,
                        suggestionsContainerOpen: classes.suggestionsContainerOpen + ' suggestionsContainerOpen',
                        suggestionsList: classes.suggestionsList,
                        suggestion: classes.suggestion
                    }}
                    renderSuggestionsContainer={options => (
                        <Paper {...options.containerProps} rounded>
                            {options.children}
                        </Paper>
                    )}
                />
            </div>
        )
    }
}

export default withTranslation()(withStyles(styles)(AutoSuggest))
